import 'lazysizes'

import { isEqual } from 'lodash'
import React from 'react'
import Imgix, { buildURL, SharedImigixAndSourceProps } from 'react-imgix'
import styled, { css } from 'styled-components'

const IMGIX_PATTERN = /https:\/\/(.+).imgix.net/
const SVG_PATTERN = /\.svg\b/

type LazyOptions = {
  placeholder?: SharedImigixAndSourceProps['imgixParams'] & {
    blur?: string
    scale?: number
  }
  transform?: string
}

type ImageProps = SharedImigixAndSourceProps & { lazy?: boolean | LazyOptions }

const DEFAULT_PLACEHOLDER_BLUR = '16px'
const DEFAULT_PLACEHOLDER_SCALE = 1.05
const DEFAULT_PLACEHOLDER_TRANSFORM = ''

const ImgixImage = styled(Imgix)`
  ${({ lazy = {} }: ImageProps) => {
    let blur = DEFAULT_PLACEHOLDER_BLUR
    let scale = DEFAULT_PLACEHOLDER_SCALE
    let transform = DEFAULT_PLACEHOLDER_TRANSFORM

    if (typeof lazy === 'object') {
      if (lazy.placeholder?.blur) {
        blur = lazy.placeholder?.blur
      }
      if (lazy.placeholder?.scale) {
        scale = lazy.placeholder.scale
      }
      if (lazy?.transform) {
        transform = lazy.transform
      }
    }

    return css`
      &.lazyload,
      &.lazyloading {
        filter: blur(${blur});
        transform: ${transform} scale(${scale});
      }
      &.lazyloaded {
        transition: transform 0.4s ease, filter 0.4s;
        filter: blur(0);
        transform: ${transform} scale(1);
      }
    `
  }}
`

const Image = ({ className = '', lazy = false, ...props }: ImageProps) => {
  const { src, htmlAttributes: attrs = {} } = props

  if (!IMGIX_PATTERN.test(src) || SVG_PATTERN.test(src)) {
    let altText
    if ('alt' in attrs) {
      altText = attrs.alt
    }
    return (
      <img
        alt={altText}
        className={className}
        height={props.height}
        src={src}
        width={props.width}
      />
    )
  }

  if (!lazy) {
    return <ImgixImage {...props} className={className} src={src} />
  }

  let lqipImgixParams: ImageProps['imgixParams'] = { auto: 'format,compress', h: 16, w: 16 }
  if (typeof lazy === 'object' && lazy.placeholder) {
    const { blur: _blur, scale: _scale, ...params } = lazy.placeholder
    lqipImgixParams = { ...lqipImgixParams, ...props.imgixParams, ...params }
  }
  const lowQualityImagePlaceholder = buildURL(src, lqipImgixParams)

  return (
    <ImgixImage
      {...props}
      attributeConfig={{
        sizes: 'data-sizes',
        src: 'data-srcs',
        srcSet: 'data-srcset',
      }}
      className={`${className} lazyload`}
      htmlAttributes={{
        src: lowQualityImagePlaceholder,
      }}
      lazy={lazy}
      src={src}
    />
  )
}

export default React.memo(Image, isEqual)
